import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { LayoutList, ExternalLink, Sparkles } from 'lucide-react';
import { useTheme } from '../ThemeContext';

export const WhatsappFlowNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();

  return (
    <div className={`w-80 rounded-xl border transition-all ${
      isDark ? 'bg-slate-900 border-slate-800 shadow-xl' : 'bg-white border-slate-200 shadow-lg'
    } ${
      selected ? 'border-teal-500 ring-2 ring-teal-500/30' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      {/* Target Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`!h-3.5 !w-3.5 !border-2 !bg-teal-500 hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />

      {/* Node Header */}
      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-teal-950/80 to-emerald-900/40 border-teal-500/20'
          : 'bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-teal-500/20 text-teal-400' : 'bg-teal-100 text-teal-700'
          }`}>
            <LayoutList className="h-3.5 w-3.5" />
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? 'text-teal-200' : 'text-teal-900'
          }`}>
            {data.label || 'WhatsApp Native Flow'}
          </span>
        </div>
        <span className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${
          isDark ? 'bg-teal-500/20 text-teal-300' : 'bg-teal-100 text-teal-800'
        }`}>
          <Sparkles className="h-2.5 w-2.5" />
          FLOW FORM
        </span>
      </div>

      {/* Node Body */}
      <div className="p-3.5 space-y-3 text-xs">
        {data.header && (
          <div className={`text-[11px] font-bold ${isDark ? 'text-teal-300' : 'text-teal-700'}`}>
            {data.header}
          </div>
        )}

        <div className={`line-clamp-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          {data.body || 'Please complete the form by tapping below:'}
        </div>

        {/* Selected Flow Card */}
        <div className={`rounded-lg p-2.5 border ${
          isDark ? 'bg-slate-800/60 border-slate-700/60' : 'bg-slate-50 border-slate-200'
        }`}>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${
              isDark ? 'text-slate-400' : 'text-slate-500'
            }`}>
              Meta Flow
            </span>
            <span className={`text-[10px] font-mono font-medium ${
              data.flow_id ? 'text-teal-500' : 'text-amber-500'
            }`}>
              {data.flow_id ? `ID: ${data.flow_id}` : 'Select Flow in Drawer'}
            </span>
          </div>
          {data.flow_name && (
            <div className={`mt-1 font-semibold text-xs truncate ${
              isDark ? 'text-slate-200' : 'text-slate-800'
            }`}>
              {data.flow_name}
            </div>
          )}
        </div>

        {/* CTA Button Preview */}
        <div className="pt-1">
          <div className={`flex items-center justify-center gap-1.5 w-full py-2 px-3 rounded-lg font-semibold text-xs transition-colors shadow-sm ${
            isDark 
              ? 'bg-teal-600 hover:bg-teal-500 text-white' 
              : 'bg-teal-500 hover:bg-teal-600 text-white'
          }`}>
            <span>{data.cta_text || 'Open Form'}</span>
            <ExternalLink className="h-3 w-3" />
          </div>
          <div className="text-[10px] text-center text-slate-400 mt-1">
            Opens native in-app form in WhatsApp
          </div>
        </div>
      </div>

      {/* Success Handle (when form is submitted) */}
      <div className={`flex items-center justify-end px-3 py-1.5 border-t text-[10px] font-medium ${
        isDark ? 'border-slate-800 bg-slate-950/40 text-teal-400' : 'border-slate-100 bg-slate-50 text-teal-700'
      }`}>
        <span className="mr-2">On Form Submit</span>
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className={`!h-3.5 !w-3.5 !border-2 !bg-teal-500 hover:!scale-125 transition-transform ${
            isDark ? '!border-slate-900' : '!border-white'
          }`}
        />
      </div>
    </div>
  );
});

WhatsappFlowNode.displayName = 'WhatsappFlowNode';
